import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { FieldError } from 'components/ui/field';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { isConfidentialityChoiceValid } from 'pages/matters/closing/confidentialityRules';
import { useId, useState } from 'react';
import { CloseOnSendDto, ConfidentialityDecision } from 'types/caseClosing';
import { t } from 'utils/getTranslations';

export interface CloseOnSendConfidentialityChoice {
    decision: ConfidentialityDecision;
    reason: string | null;
}

interface Props {
    closeOnSend: Pick<
        CloseOnSendDto,
        'confidentialityDecision' | 'confidentialReason' | 'isConfidentialReasonRequired'
    >;
    onCancel: () => void;
    onSave: (choice: CloseOnSendConfidentialityChoice) => void;
}

const DECISIONS: { value: ConfidentialityDecision; labelId: string }[] = [
    { value: ConfidentialityDecision.SWITCH_OFF, labelId: 'case-closing-invoice.confidential-modal.switch-off' },
    { value: ConfidentialityDecision.KEEP, labelId: 'case-closing-invoice.confidential-modal.keep' },
];

export function CloseOnSendConfidentialityModal({ closeOnSend, onCancel, onSave }: Props) {
    const isReasonRequired = closeOnSend.isConfidentialReasonRequired;
    // Seeded once per opening: the modal edits a draft of the stored choice, saved only on Save.
    const [decision, setDecision] = useState<ConfidentialityDecision | null>(() => closeOnSend.confidentialityDecision);
    const [reason, setReason] = useState(() => closeOnSend.confidentialReason ?? '');
    const [showErrors, setShowErrors] = useState(false);
    const textId = useId();
    const reasonId = useId();

    const isKeep = decision === ConfidentialityDecision.KEEP;
    const requiredError = { message: t('validation.required-field') };

    function handleSave() {
        if (decision === null || !isConfidentialityChoiceValid(true, decision, reason, isReasonRequired)) {
            setShowErrors(true);
            return;
        }
        const trimmedReason = reason.trim();
        onSave({ decision, reason: isKeep && trimmedReason !== '' ? trimmedReason : null });
    }

    return (
        <>
            <ModalHeader
                kit
                title={t('case-closing-invoice.confidential-modal.title')}
                description={t('case-closing-invoice.confidential-modal.description')}
                onCancel={onCancel}
            />
            <div className="new:px-6 new:py-6 flex flex-col gap-4 overflow-y-auto px-[23px] py-[15px]">
                {/* The new design says it once, in the header's description; here it still names the choice. */}
                <p id={textId} className="text-body2 new:sr-only">
                    {t('case-closing-invoice.confidential-modal.text')}
                </p>
                <div className="flex flex-col gap-1">
                    <RadioGroup
                        aria-labelledby={textId}
                        value={decision}
                        onValueChange={(value) => setDecision(value as ConfidentialityDecision)}
                        className="gap-0"
                    >
                        {DECISIONS.map(({ value, labelId }) => (
                            <label
                                key={value}
                                className="text-body2 new:text-body1 flex h-8 w-fit cursor-pointer items-center gap-2.5"
                            >
                                <RadioGroupItem value={value} aria-invalid={showErrors && decision === null} />
                                {t(labelId)}
                            </label>
                        ))}
                    </RadioGroup>
                    <FieldError errors={showErrors && decision === null ? [requiredError] : undefined} />
                </div>
                {isKeep ? (
                    <div className="flex flex-col gap-1">
                        <label htmlFor={reasonId} className="text-fg-secondary text-sm">
                            {t('case-closing-invoice.confidential-modal.reason')}
                            {isReasonRequired ? <span className="text-destructive">&thinsp;*</span> : null}
                        </label>
                        <Textarea
                            id={reasonId}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            aria-invalid={showErrors && isReasonRequired && reason.trim() === ''}
                        />
                        <FieldError
                            errors={
                                showErrors && isReasonRequired && reason.trim() === '' ? [requiredError] : undefined
                            }
                        />
                    </div>
                ) : null}
                <p className="text-body2 new:text-meta text-fg-secondary">
                    {t('case-closing-invoice.confidential-modal.note')}
                </p>
            </div>
            <ModalFooter kit onCancel={onCancel} onSave={handleSave} />
        </>
    );
}
